import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, Col, Flex, InputNumber, Progress, Result, Row, Select, Skeleton, Space, Steps, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { useParams } from "react-router";
import { ApiError, api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { ScanInput } from "@/components/ScanInput";
import { useTabTitle } from "@/components/tabs/WorkTabs";
import type { ScanResult } from "@/lib/scanSound";
import { useI18n } from "@/i18n";
import { SkuThumb } from "@/components/SkuThumb";
import { MethodTag, TaskStatusTag, ZoneTag, type Task } from "./common";
import { LineOrders, PickPanel } from "./PickPanel";
import { PaperPick } from "./PaperPick";

type Line = NonNullable<Task["lines"]>[number];

/**
 * A warehouse task worked by scanning. Putaway, as on a handheld: scan the
 * SKU, set the quantity, then scan the destination bin, which saves the line
 * (serial SKUs: scan the bin, then each unit, then save). The bin can also be
 * picked from the list or taken from the suggestion. Picking: line by line in
 * walking order (PickPanel).
 */
export function TaskDetail() {
  const { taskID = "" } = useParams();
  const { t, formatNumber } = useI18n();
  const { can } = useAuth();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const [lineId, setLineId] = useState<string | null>(null);
  const [toCode, setToCode] = useState<string | null>(null);
  const [wholeTo, setWholeTo] = useState<string | null>(null);
  const [qty, setQty] = useState<number | null>(null);
  const [serials, setSerials] = useState<string[]>([]);

  const task = useQuery({
    queryKey: ["tasks", "detail", taskID],
    queryFn: () => unwrap(api.GET("/v1/tasks/{taskID}", { params: { path: { taskID } } })),
    retry: (n, err) => !(err instanceof ApiError && err.status === 404) && n < 2,
  });
  useTabTitle(task.data?.number);
  const x = task.data;
  const lines = x?.lines ?? [];
  const line = lines.find((l) => l.id === lineId) ?? null;
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["tasks"] });
    void queryClient.invalidateQueries({ queryKey: ["inventory"] });
    void queryClient.invalidateQueries({ queryKey: ["locations"] });
  };
  const pick = (l: Line) => {
    if (x?.kind === "picking") {
      setLineId(l.id);
      return;
    }
    setLineId(l.id);
    setToCode(null);
    setQty(l.quantity - l.doneQty);
    setSerials([]);
  };

  const confirm = useMutation({
    mutationFn: (to: string) =>
      unwrap(
        api.POST("/v1/tasks/{taskID}/lines/{lineID}/putaway", {
          params: { path: { taskID, lineID: line!.id } },
          body: { toCode: to, ...(line!.tracking === "serial" ? { serials } : { quantity: qty ?? 0 }) },
        }),
      ).then((res) => ({ res, to })),
    onSuccess: ({ res, to }) => {
      void message.success(t("wms.task.saved", { code: to }));
      if (res.status === "done") void message.success(t("wms.task.allDone"));
      queryClient.setQueryData(["tasks", "detail", taskID], res);
      setLineId(null);
      setToCode(null);
      setSerials([]);
      refresh();
    },
    onError: (err) => {
      // A bin that does not exist or cannot take goods: scan another.
      setToCode(null);
      void message.error(errorText(err));
    },
  });
  const locs = useQuery({
    queryKey: ["locations", task.data?.warehouseId, "all"],
    queryFn: () =>
      unwrap(api.GET("/v1/warehouses/{warehouseID}/locations", { params: { path: { warehouseID: task.data!.warehouseId }, query: { status: "active" } } })),
    enabled: task.data?.kind === "putaway",
  });
  // Sellable goods go to shelves, damaged and quarantined to their own places
  // (as the server checks); a bin being counted takes nothing.
  const binKind = { sellable: "storage", damaged: "damaged", quarantine: "quarantine" } as const;
  const binOptions = (locs.data?.items ?? [])
    .filter((l) => line && l.kind === binKind[line.condition] && !l.countId)
    .map((l) => ({ value: l.code, label: l.code }));
  // A pallet goes whole into one bin; its goods were scanned onto it at receiving.
  const whole = useMutation({
    mutationFn: (to: string) => unwrap(api.POST("/v1/tasks/{taskID}/putaway-all", { params: { path: { taskID } }, body: { toCode: to } })),
    onSuccess: (res) => {
      void message.success(t("wms.plt.placed", { code: wholeTo ?? "" }));
      queryClient.setQueryData(["tasks", "detail", taskID], res);
      setWholeTo(null);
      refresh();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const shelves = (locs.data?.items ?? []).filter((l) => l.kind === "storage" && !l.countId).map((l) => ({ value: l.code, label: l.code }));
  const cancel = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/tasks/{taskID}/cancel", { params: { path: { taskID } } })),
    onSuccess: refresh,
    onError: (err) => void message.error(errorText(err)),
  });

  if (task.isError) {
    return task.error instanceof ApiError && task.error.status === 404 ? <Result status="404" title={t("page.notFound.title")} /> : <Result status="error" title={errorText(task.error)} />;
  }
  if (!x) return <Skeleton active />;
  const open = x.status === "open" || x.status === "in_progress";

  // One scan field for the whole flow: an SKU chooses its line, a code after
  // that is the destination bin (which saves the line once the quantity is
  // set), and for serial SKUs further codes are units.
  const setBin = (code: string): ScanResult => {
    const to = code.toUpperCase();
    setToCode(to);
    if (line && line.tracking !== "serial" && (qty ?? 0) > 0 && !confirm.isPending) return confirm.mutateAsync(to);
    return true;
  };
  const scan = (code: string): ScanResult => {
    const bySku = lines.find((l) => l.doneQty < l.quantity && (l.skuCode.toUpperCase() === code.toUpperCase() || l.barcode === code));
    if (!line || (bySku && !toCode)) {
      if (bySku) {
        pick(bySku);
        return true;
      }
      void message.warning(t("error.code_not_found"));
      return false;
    }
    if (!toCode) return setBin(code);
    if (line.tracking === "serial") {
      setSerials((s) => (s.includes(code) ? s : [...s, code]));
      return true;
    }
    return false;
  };
  const ready = line && toCode && (line.tracking === "serial" ? serials.length > 0 : (qty ?? 0) > 0);
  const step = !line ? 0 : toCode ? 2 : 1;
  const scanHint = !line ? t("wms.task.scanSku") : !toCode ? t("wms.task.scanBin") : line.tracking === "serial" ? t("wms.task.scanSerial") : t("wms.task.scanSku");

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space align="center" size={12} wrap>
          <Typography.Title level={2} className="z-num" style={{ margin: 0, fontSize: 28 }}>
            {x.number}
          </Typography.Title>
          <TaskStatusTag status={x.status} />
          {x.kind === "picking" && x.method && <MethodTag method={x.method} />}
          <ZoneTag zone={x.zone} />
          <Typography.Text type="secondary">
            {t(`wms.task.title.${x.kind}`)} · {x.warehouseName}
            {x.assignedName ? ` · ${x.assignedName}` : ""}
          </Typography.Text>
        </Space>
        {open && can("warehouse.manage") && (
          <Button danger onClick={() => modal.confirm({ title: t("wms.task.cancel"), content: t("wms.task.cancelConfirm", { number: x.number }), okButtonProps: { danger: true }, cancelText: t("common.close"), onOk: () => cancel.mutateAsync() })}>
            {t("wms.task.cancel")}
          </Button>
        )}
      </Flex>
      <Progress percent={Math.round((x.doneUnits / Math.max(x.units, 1)) * 100)} format={() => `${formatNumber(x.doneUnits)}/${formatNumber(x.units)}`} style={{ marginBottom: 16 }} />
      <Row gutter={[16, 16]}>
        {open && x.kind === "putaway" && x.container?.kind === "pallet" && (
          <Col xs={24}>
            <Card size="small" title={t("wms.plt.wholeTitle", { code: x.container.code })}>
              <Flex gap={8} wrap align="center">
                <Typography.Text type="secondary">{t("wms.plt.wholeHelp")}</Typography.Text>
                <Select<string> showSearch style={{ width: 220 }} placeholder={t("wms.task.to")} value={wholeTo ?? undefined} onChange={setWholeTo} options={shelves} />
                <Button type="primary" disabled={!wholeTo} loading={whole.isPending} onClick={() => wholeTo && whole.mutate(wholeTo)}>
                  {t("wms.plt.placeAll")}
                </Button>
              </Flex>
            </Card>
          </Col>
        )}
        {open && x.kind === "putaway" && (
          <Col xs={24} xl={8}>
            <Card size="small" title={t("wms.task.start")}>
              <Space orientation="vertical" size={12} style={{ width: "100%" }}>
                <Steps size="small" current={step} items={[{ title: t("wms.task.stepSku") }, { title: t("wms.task.stepQty") }, { title: t("wms.task.stepBin") }]} />
                <ScanInput onScan={scan} placeholder={scanHint} onEmptyEnter={() => (line && !toCode && line.toCode ? setBin(line.toCode) : undefined)} />
                {line && (
                  <Card size="small" type="inner" title={<span className="z-num">{line.skuCode}</span>}>
                    <Space orientation="vertical" size={8} style={{ width: "100%" }}>
                      <Typography.Text>{line.skuName}</Typography.Text>
                      <Typography.Text type="secondary">
                        {t("wms.task.from")} <span className="z-num">{line.fromCode}</span>
                        {line.toCode && (
                          <>
                            {" · "}
                            {t("wms.task.suggested")} <span className="z-num">{line.toCode}</span>
                          </>
                        )}
                      </Typography.Text>
                      {line.tracking !== "serial" && (
                        <InputNumber prefix={t("wms.in.quantity")} min={1} max={line.quantity - line.doneQty} precision={0} value={qty} onChange={setQty} style={{ width: "100%" }} />
                      )}
                      <Flex vertical gap={6}>
                        <Typography.Text strong>{t("wms.task.to")}</Typography.Text>
                        {toCode ? (
                          <Flex gap={8} align="center">
                            <Tag color="processing" className="z-num" style={{ fontSize: 15, padding: "2px 10px" }}>
                              {toCode}
                            </Tag>
                            <Button size="small" type="link" onClick={() => setToCode(null)}>
                              {t("wms.task.changeBin")}
                            </Button>
                          </Flex>
                        ) : (
                          <>
                            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                              {t("wms.task.binHelp")}
                            </Typography.Text>
                            <Select<string>
                              showSearch
                              placeholder={t("wms.task.pickBin")}
                              options={binOptions}
                              loading={locs.isPending}
                              onChange={setBin}
                              value={null}
                              style={{ width: "100%" }}
                              notFoundContent={t("wms.task.noBins")}
                            />
                            {line.toCode && (
                              <Button block onClick={() => setBin(line.toCode!)}>
                                {t("wms.task.useSuggested", { code: line.toCode })}
                              </Button>
                            )}
                          </>
                        )}
                      </Flex>
                      {line.tracking === "serial" && (
                        <Space wrap>
                          {serials.map((s) => (
                            <Tag key={s} closable onClose={() => setSerials((v) => v.filter((y) => y !== s))} className="z-num">
                              {s}
                            </Tag>
                          ))}
                        </Space>
                      )}
                      <Button type="primary" block disabled={!ready} loading={confirm.isPending} onClick={() => confirm.mutate(toCode!)}>
                        {t("wms.task.confirm")}
                      </Button>
                    </Space>
                  </Card>
                )}
              </Space>
            </Card>
          </Col>
        )}
        {open && x.kind === "picking" && (
          <Col xs={24} xl={8}>
            <Space orientation="vertical" size={16} style={{ width: "100%" }}>
              <PickPanel task={x} lineId={lineId} onLine={setLineId} />
              <PaperPick task={x} />
            </Space>
          </Col>
        )}
        <Col xs={24} xl={open ? 16 : 24}>
          <Table<Line>
            size="small"
            rowKey="id"
            dataSource={lines}
            pagination={false}
            onRow={(l) => ({ onClick: () => open && l.doneQty < l.quantity && pick(l), style: { cursor: open ? "pointer" : undefined, background: l.id === lineId ? "var(--z-mint)" : undefined } })}
            columns={[
              { title: t("catalog.code"), dataIndex: "skuCode", render: (v: string, l) => <Space><SkuThumb skuId={l.skuId} size={36} /><Space orientation="vertical" size={0}><span className="z-num">{v}</span><Typography.Text type="secondary" style={{ fontSize: 12 }}>{l.skuName}</Typography.Text></Space></Space> },
              { title: t("inventory.bin.lot"), dataIndex: "lotNo", width: 150, render: (v: string | null, l) => (v ? `${v}${l.expiresOn ? ` · ${l.expiresOn}` : ""}` : "—") },
              { title: t("wms.task.from"), dataIndex: "fromCode", width: 120, render: (v: string) => <span className="z-num">{v}</span> },
              x.kind === "picking"
                ? { title: t("wms.pick.for"), width: 220, render: (_, l) => <LineOrders line={l} method={x.method} /> }
                : { title: t("wms.task.suggested"), dataIndex: "toCode", width: 120, render: (v: string | null) => <span className="z-num">{v ?? "—"}</span> },
              {
                title: t("wms.task.progress"),
                width: 130,
                align: "right",
                render: (_, l) => (l.doneQty >= l.quantity ? <Tag color="success">{t("wms.task.lineDone")}</Tag> : <span className="z-num">{l.doneQty}/{l.quantity}</span>),
              },
            ]}
          />
        </Col>
      </Row>
    </>
  );
}
